/**
 * Hook to register Dashboard tab with DetailPageTabsContext
 * 
 * This hook automatically adds the Dashboard tab to any doctype detail page.
 * It's generic and reusable - works for all doctypes.
 * 
 * Best Practices:
 * - Uses functional updates for setTabs (prev => ...) to avoid dependency issues
 * - No dependency on tabs array (prevents loops/glitches)
 * - Automatically manages tab lifecycle (add/remove based on conditions)
 * - Uses constant for tab ID to ensure consistency
 * - Positions Dashboard before Inventory tab (for Item) or after first tab (for others)
 */

import { useEffect } from 'react';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { useLanguage } from '@/i18n/useLanguage';
import { getTabLabelTranslation } from '@/lib/views/labels/translations';

/**
 * Tab ID constant - use this everywhere you reference the Dashboard tab
 */
export const DASHBOARD_TAB_ID = 'dashboard';

interface UseDashboardTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean; // Only show for existing documents
}

/**
 * Hook to register Dashboard tab with DetailPageTabsContext.
 * 
 * This makes the Dashboard tab available in the tab navigation
 * for any doctype detail page.
 * 
 * The tab is automatically:
 * - Added when document has a name (not new)
 * - Removed when document is new
 * - Positioned before Inventory tab (for Item) or after first tab (for others)
 * 
 * Uses functional updates to prevent dependency issues and tab duplication.
 */
export function useDashboardTab({ 
  doctype, 
  name, 
  enabled = true 
}: UseDashboardTabOptions) {
  const { setTabs } = useDetailPageTabs();
  const { language } = useLanguage();
  const dashboardLabel = getTabLabelTranslation("Dashboard", language);
  
  useEffect(() => {
    const shouldShow = enabled && !!doctype && !!name && name !== 'new';
    
    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some(t => t.name === DASHBOARD_TAB_ID);
      
      // Remove tab if conditions not met and exists
      if (!shouldShow && exists) {
        return prevTabs.filter(t => t.name !== DASHBOARD_TAB_ID);
      }
      
      // Add tab if conditions met and doesn't exist
      if (shouldShow && !exists) {
        // Create a TabStructure for Dashboard tab
        // It has empty sections so TabRenderer won't try to render it
        const dashboardTab: TabStructure = {
          name: DASHBOARD_TAB_ID,
          label: dashboardLabel || 'Dashboard',
          sections: [], // Empty sections - this tab is handled separately in SystemView
        };
        
        // Find Inventory tab index (for Item) or use first tab as reference
        // Inventory tab typically has label "Inventory" or name containing "inventory"
        const inventoryIndex = prevTabs.findIndex(
          t => t.name.toLowerCase().includes('inventory') || 
               t.label.toLowerCase().includes('inventory')
        );
        
        if (inventoryIndex !== -1) {
          // Insert Dashboard before Inventory tab
          return [
            ...prevTabs.slice(0, inventoryIndex),
            dashboardTab,
            ...prevTabs.slice(inventoryIndex),
          ];
        } else {
          // No Inventory tab found - insert Dashboard after first tab
          // If no tabs exist, just add it
          if (prevTabs.length === 0) {
            return [dashboardTab];
          }
          return [prevTabs[0], dashboardTab, ...prevTabs.slice(1)];
        }
      }

      // Update label if tab exists (e.g., language changed)
      if (shouldShow && exists) {
        return prevTabs.map((t) =>
          t.name === DASHBOARD_TAB_ID
            ? { ...t, label: dashboardLabel || t.label || "Dashboard" }
            : t
        );
      }
      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs, dashboardLabel]);
  
  return {
    tabId: DASHBOARD_TAB_ID,
    isEnabled: enabled && !!name && name !== 'new' && !!doctype,
  };
}
